import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  MIN_SESSION_TIMEOUT_MINUTES,
  MAX_SESSION_TIMEOUT_MINUTES,
} from "../Utils/sessionTimeoutConfig";
import Actions from "../Redux/action";
import { getSessionTimeoutMinutes } from "../Redux/reducer/SessionTimeoutReducer";
import { DeclinePopup } from "../component/DeclinePopup";

const PRESET_OPTIONS = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "60 minutes", value: 60 },
  { label: "2 hours", value: 120 },
  { label: "4 hours", value: 240 },
  { label: "8 hours", value: 480 },
];

export const SessionTimeoutConfig = () => {
  const dispatch = useDispatch();
  const timeoutMinutes = useSelector((state) => getSessionTimeoutMinutes(state));
  const [inputValue, setInputValue] = useState(timeoutMinutes.toString());
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  useEffect(() => {
    dispatch(Actions.getSessionTimeoutAction());
  }, [dispatch]);

  useEffect(() => {
    setInputValue(timeoutMinutes.toString());
  }, [timeoutMinutes]);

  const handleSave = () => {
    const value = parseInt(inputValue, 10);
    if (isNaN(value) || value < MIN_SESSION_TIMEOUT_MINUTES || value > MAX_SESSION_TIMEOUT_MINUTES) {
      toast.error(`Please enter a value between ${MIN_SESSION_TIMEOUT_MINUTES} and ${MAX_SESSION_TIMEOUT_MINUTES} minutes`);
      return;
    }
    if (value === timeoutMinutes) {
      toast.info("No change to save.");
      return;
    }
    setPendingChange(value);
  };

  const handlePresetClick = (minutes) => {
    if (isSaving) return;
    if (minutes === timeoutMinutes) {
      toast.info("This is already the current setting.");
      return;
    }
    setPendingChange(minutes);
  };

  const handleConfirmChange = () => {
    if (!pendingChange) return;
    const value = pendingChange;
    setPendingChange(null);
    setIsSaving(true);
    dispatch(
      Actions.updateSessionTimeoutAction(
        { session_timeout_minutes: value },
        (response) => {
          setIsSaving(false);
          if (response?.status_code === 0) {
            const saved = response?.response?.session_timeout_minutes ?? value;
            setInputValue(saved.toString());
            setIsSaved(true);
            toast.success(`Session timeout updated to ${saved} minutes. This applies to all users.`);
            setTimeout(() => setIsSaved(false), 2000);
          } else {
            const msg = response?.message || response?.response?.data?.message || "Failed to update session timeout";
            toast.error(msg);
          }
        }
      )
    );
  };

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <h5 className="card-title mb-2">Session Timeout Configuration</h5>
        <p className="text-muted mb-0">
          Configure how long a user can remain inactive before being automatically logged out.
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h6 className="mb-3">Current Setting</h6>
              <p className="text-muted mb-4">
                Session timeout: <strong>{timeoutMinutes} minutes</strong>
                {timeoutMinutes >= 60 && (
                  <span className="text-muted"> ({Math.floor(timeoutMinutes / 60)} hour{timeoutMinutes > 60 ? 's' : ''})</span>
                )}
              </p>

              <div className="mb-4">
                <label htmlFor="timeoutInput" className="form-label">
                  Session Timeout (minutes)
                </label>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="number"
                    id="timeoutInput"
                    className="form-control"
                    style={{ maxWidth: '120px' }}
                    min={MIN_SESSION_TIMEOUT_MINUTES}
                    max={MAX_SESSION_TIMEOUT_MINUTES}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Saving...
                      </>
                    ) : isSaved ? (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Saved
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
                <small className="text-muted">
                  Min: {MIN_SESSION_TIMEOUT_MINUTES} min, Max: {MAX_SESSION_TIMEOUT_MINUTES} min (8 hours)
                </small>
              </div>

              <h6 className="mb-2">Quick adjustment</h6>
              <div className="d-flex flex-wrap gap-2">
                {PRESET_OPTIONS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`btn btn-sm ${timeoutMinutes === preset.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePresetClick(preset.value)}
                    disabled={isSaving}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-md-6">
              <div className="alert alert-info mb-0">
                <i className="fas fa-info-circle me-2"></i>
                <strong>How it works:</strong>
                <ul className="mb-0 mt-2 ps-3">
                  <li>Users are automatically logged out after the configured period of inactivity.</li>
                  <li>Any user interaction (clicks, keystrokes) resets the inactivity timer.</li>
                  <li><strong>Admin changes apply to all users globally.</strong> </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {pendingChange != null && (
        <DeclinePopup
          title={`Are you sure you want to change the session timeout to ${pendingChange} minutes? This will apply to all users.`}
          onClose={() => setPendingChange(null)}
          onSuccess={handleConfirmChange}
        />
      )}
    </div>
  );
};
